import React, { MouseEventHandler, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { useHistory } from 'react-router';
import { theme } from '~/theme';

import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import AccommodationPropertyRatesResultsTable from '~/components/Accommodation/Pages/PropertyDetails/sections/AccommodationPropertyRates/AccommodationPropertyRatesResultsTable';
import { sources } from '~/components/Accommodation/constants';

import { allRegions } from '~/consts/allRegions';
import { brands } from '~/consts/brands';

import useQuery from '~/hooks/useQuery';

import {
  AvailabilityResponse,
  DebugInfo,
  MergedRate,
  Rate,
  RoomRate,
  getAvailability,
  getAvailabilityDebug,
} from '~/services/AccommodationService';
import { Dayjs, newDate } from '~/services/TimeService';

import { filterRates } from '~/utils/tests/filterRoomRates';

import BestAvailableRate from './BestAvailableRate';
import DebugInformation from './DebugInformation/DebugInformation';

type RatesFilter = 'hotel-only' | 'hotel-with-flights' | 'all';
export type RoomRatesWithRates = Record<
  string,
  RoomRate & {
    rates: Array<Rate>;
  }
>;

function parseRatesFilter(value: string | undefined): RatesFilter {
  switch (value) {
    case 'hotel-only':
    case 'hotel-with-flights':
    case 'all':
      return value;
    default:
      if (value) {
        console.warn(`Parsed unknown rates filter value: ${value}`);
      }
      return 'hotel-only';
  }
}

export const getMergedRateKey = (rate: Rate) => {
  return `${rate.roomId}-${rate.groupKey}`;
};

export const getOccupancyString = (adults: number, childrenAges: Array<number>) => {
  let result = `${adults}`;
  if (childrenAges.length > 0) {
    result += `-${childrenAges.join(',')}`;
  }
  return result;
};

export default function AccommodationPropertyRatesSection() {
  const query = useQuery();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { propertyDetails } = useAccommodationPropertyDetailsContext();

  const [checkIn, setCheckIn] = useState<Dayjs | null>(query.get('checkIn') ? newDate(query.get('checkIn')) : null);
  const [checkOut, setCheckOut] = useState<Dayjs | null>(query.get('checkOut') ? newDate(query.get('checkOut')) : null);
  const [dynamicAvailability, setDynamicAvailability] = useState(query.get('dynamicAvailability') === 'true');
  const [allRates, setAllRates] = useState(query.get('allRates') === 'true');
  const [useV2Pricing, setUseV2Pricing] = useState(query.get('useV2') === 'true');
  const [useSource, setUseSource] = useState(query.get('source') ?? null);
  const [doDebug, setDoDebug] = useState(query.get('doDebug') === 'true');
  const [region, setRegion] = useState<App.MembershipRegion>((query.get('region') as App.MembershipRegion) ?? 'AU');
  const [currency, setCurrency] = useState<string>(query.get('currency') ?? 'AUD');
  const [brand, setBrand] = useState<App.Brands>((query.get('brand') as App.Brands) ?? 'luxuryescapes');
  const [adults, setAdults] = useState(query.get('adults') ? parseInt(query.get('adults')) : 2);
  const [childrenAges, setChildrenAges] = useState<Array<number>>(
    query.get('childrenAges') ? query.get('childrenAges').split(',').map(Number) : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [debugResponse, setDebugResponse] = useState<{
    response: AvailabilityResponse | null;
    debug: DebugInfo | null;
  } | null>(null);

  const [ratesFilter, setRatesFilter] = useState<RatesFilter>(parseRatesFilter(query.get('ratesFilter')));
  const onRatesFilterChange = useCallback((_, newRatesFilter: RatesFilter | null) => {
    // Prevent toggle button from being deselected (i.e. `newRatesFilter` being `null`).
    if (newRatesFilter) {
      setRatesFilter(newRatesFilter);
    }
  }, []);

  const [noRatesFound, setNoRatesFound] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ReactNode>(null);
  const [modalTitle, setModalTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Remove query parameters after initial state setup
  useEffect(() => {
    if (query.toString()) {
      history.replace(window.location.pathname);
    }
  }, [query, history]);

  const handleSearch = async () => {
    setIsLoading(true);
    setError(null);
    setNoRatesFound(false);
    setDebugResponse(null);

    try {
      let availabilityResponse: AvailabilityResponse | null = null;
      let debug: DebugInfo | null = null;
      const payload = {
        checkIn: checkIn?.format('YYYY-MM-DD') ?? '',
        checkOut: checkOut?.format('YYYY-MM-DD') ?? '',
        occupancies: [getOccupancyString(adults, childrenAges)],
        propertyIds: [propertyDetails.id],
        region,
        currency,
        brand,
        allRates,
        dynamicAvailability,
        useV2: useV2Pricing,
        source: useSource ?? undefined,
      };
      if (doDebug) {
        const response = await getAvailabilityDebug(payload);

        if (response.status !== 200) {
          setError('Failed to fetch data');
          return;
        }
        availabilityResponse = response.result.response;
        debug = response.result.debug;
      } else {
        const response = await getAvailability(payload);
        if (response.status !== 200) {
          setError('Failed to fetch data');
          return;
        }
        availabilityResponse = response.result;
      }
      if (
        Object.entries(availabilityResponse?.properties).length === 0 ||
        Object.entries(availabilityResponse?.properties[propertyDetails.id].rooms).length === 0
      ) {
        setNoRatesFound(true);
        setIsLoading(false);
        return;
      }
      setDebugResponse({ response: availabilityResponse, debug });
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = useCallback((title: string, content: ReactNode) => {
    setModalTitle(title);
    setModalContent(content);
    setModalOpen(true);
  }, []);

  const { roomRates, hotelOnlyRates, hotelWithFlightRates, lowestPricedRoomRate } = useMemo(() => {
    if (!debugResponse) {
      return { roomRates: null, lowestPricedRoomRate: null };
    }
    const { roomRates, hotelOnlyRates, hotelWithFlightRates } = filterRates(
      debugResponse.response.properties[propertyDetails.id].rooms,
    );

    const propertyLowestRate = debugResponse.response.properties[propertyDetails.id].lowestPrice;
    const lowestPricedRoomRate = propertyLowestRate
      ? {
          rate: propertyLowestRate,
          roomName: roomRates[propertyLowestRate.roomId].name,
        }
      : null;
    return { roomRates, hotelOnlyRates, hotelWithFlightRates, lowestPricedRoomRate };
  }, [debugResponse, propertyDetails.id]);

  const filteredRoomRates = useMemo(() => {
    switch (ratesFilter) {
      case 'hotel-only':
        return hotelOnlyRates;
      case 'hotel-with-flights':
        return hotelWithFlightRates;
      case 'all':
        return roomRates;
      default: {
        const exhaustivenessCheck: never = ratesFilter;
        throw new Error(`Unhandled rates filter value: ${exhaustivenessCheck}`);
      }
    }
  }, [ratesFilter, hotelOnlyRates, hotelWithFlightRates, roomRates]);

  const groupedMergedRates = useMemo(() => {
    if (!debugResponse) {
      return {};
    }
    if (!debugResponse.debug) {
      return {};
    }
    const mergedRates = debugResponse.debug.mergedRates.filter((rate) => rate.reason === 'merged-rate');
    const groupedMergedRates = mergedRates.reduce<Record<string, Array<MergedRate>>>((acc, rate) => {
      const key = getMergedRateKey(rate.rate as Rate);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(rate);
      return acc;
    }, {});
    return groupedMergedRates;
  }, [debugResponse]);

  const handleCopyToClipboardClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    const searchParams = qs.stringify({
      checkIn: checkIn?.format('YYYY-MM-DD') ?? '',
      checkOut: checkOut?.format('YYYY-MM-DD') ?? '',
      adults,
      childrenAges: childrenAges.length > 0 ? childrenAges.join(',') : undefined,
      region,
      brand,
      currency,
      doDebug,
      ratesFilter,
      allRates,
      dynamicAvailability,
      useV2: useV2Pricing,
      source: useSource ?? undefined,
    });
    const url = `${window.location.origin}/accommodation/properties/${propertyDetails.id}/rates?${searchParams}`;
    navigator.clipboard.writeText(url);
    enqueueSnackbar('Copied url to clipboard', { variant: 'info' });
  }, [
    propertyDetails.id,
    checkIn,
    checkOut,
    adults,
    region,
    currency,
    brand,
    childrenAges,
    doDebug,
    ratesFilter,
    allRates,
    dynamicAvailability,
    useV2Pricing,
    useSource,
    enqueueSnackbar,
  ]);

  return (
    <>
      <Stack spacing={4}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack flex={1} minWidth={240}>
              <DatePicker
                label="Check-in Date"
                format="DD/MM/YYYY"
                value={checkIn}
                onChange={(newValue) => setCheckIn(newValue)}
                slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
                disablePast
              />
            </Stack>

            <Stack flex={1} minWidth={240}>
              <DatePicker
                label="Check-out Date"
                format="DD/MM/YYYY"
                value={checkOut}
                onChange={(newValue) => setCheckOut(newValue)}
                slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
                disablePast
                minDate={checkIn ? checkIn.add(1, 'day') : undefined}
              />
            </Stack>
          </LocalizationProvider>

          <Stack direction="row" spacing={1} flex={2} minWidth={240}>
            <TextField
              label="Adults"
              type="number"
              value={adults}
              onChange={(e) => setAdults(parseInt(e.target.value))}
              inputProps={{ min: 1 }}
              size="small"
              fullWidth
            />
            <Box sx={{ minWidth: '450px' }}>
              <TextField
                fullWidth
                label="Children ages"
                type="number"
                placeholder="Type age and press Enter"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const input = e.target as HTMLInputElement;
                    const value = parseInt(input.value);
                    if (!isNaN(value)) {
                      setChildrenAges([...childrenAges, value]);
                      input.value = '';
                    }
                  }
                }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {childrenAges.map((age, index) => (
                  <Chip
                    key={`${age}-${index}`}
                    label={age}
                    onDelete={() => {
                      const newAges = [...childrenAges];
                      newAges.splice(index, 1);
                      setChildrenAges(newAges);
                    }}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <Stack flex={2} maxWidth={160}>
            <Select
              fullWidth
              value={region}
              onChange={(e) => {
                setRegion(e.target.value as App.MembershipRegion);
                setCurrency(allRegions.find((r) => r.code === e.target.value)?.currencyCode ?? 'AUD');
              }}
              label="Region"
            >
              {allRegions.map((region) => (
                <MenuItem key={region.code} value={region.code}>
                  {region.name}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <Stack flex={1} maxWidth={160}>
            <Select fullWidth value={brand} onChange={(e) => setBrand(e.target.value as App.Brands)} label="Brand">
              {brands.map((brand) => (
                <MenuItem key={brand.value} value={brand.value}>
                  {brand.title}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <Stack minWidth={120}>
            <FormControlLabel
              control={
                <Checkbox checked={dynamicAvailability} onChange={(e) => setDynamicAvailability(e.target.checked)} />
              }
              label={
                <Tooltip title="Check to simulate offer page. Uncheck to simulate search result.">
                  <Stack direction="row" gap={0.5}>
                    <Typography>Dynamic Availability</Typography>
                    <Typography color="textSecondary">
                      <HelpOutlineIcon fontSize="small" />
                    </Typography>
                  </Stack>
                </Tooltip>
              }
            />
          </Stack>

          <Stack minWidth={120}>
            <FormControlLabel
              control={<Checkbox checked={allRates} onChange={(e) => setAllRates(e.target.checked)} />}
              label="All Rates"
            />
          </Stack>

          <Stack minWidth={120}>
            <FormControlLabel
              control={<Checkbox checked={useV2Pricing} onChange={(e) => setUseV2Pricing(e.target.checked)} />}
              label="Use V2 Pricing"
            />
          </Stack>

          <Stack minWidth={120}>
            <Select label="Source" value={useSource} onChange={(e) => setUseSource(e.target.value)}>
              <MenuItem value={null}>Default</MenuItem>
              {sources.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <Stack minWidth={120}>
            <FormControlLabel
              control={<Checkbox checked={doDebug} onChange={(e) => setDoDebug(e.target.checked)} />}
              label="Debug"
            />
          </Stack>

          <Stack minWidth={120}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              fullWidth
              disabled={!checkIn || !checkOut || isLoading}
            >
              Search
            </Button>
          </Stack>

          <Stack minWidth={120}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyToClipboardClick}
              fullWidth
              disabled={!checkIn || !checkOut}
            >
              Share
            </Button>
          </Stack>
        </Stack>
        {isLoading && (
          <Box sx={{ p: 2 }} textAlign="center">
            <LinearProgress variant="indeterminate" />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {debugResponse && <DebugInformation availabilityDebugResponse={debugResponse} />}
        {lowestPricedRoomRate && (
          <BestAvailableRate
            rate={lowestPricedRoomRate.rate}
            roomName={lowestPricedRoomRate.roomName}
            openModal={openModal}
            currency={currency}
          />
        )}
        {filteredRoomRates && (
          <>
            <ToggleButtonGroup exclusive onChange={onRatesFilterChange} value={ratesFilter}>
              <ToggleButton value="hotel-only">Hotel</ToggleButton>
              <ToggleButton value="hotel-with-flights">Hotel + flights</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
            <AccommodationPropertyRatesResultsTable
              roomRates={filteredRoomRates}
              openModal={openModal}
              groupedMergedRates={groupedMergedRates}
              currency={currency}
              timezone={debugResponse?.response?.properties[propertyDetails.id].timezone ?? 'Australia/Sydney'}
            />
          </>
        )}
        {noRatesFound && (
          <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1 }} textAlign="center">
            <Typography>No rates found for the selected dates</Typography>
          </Box>
        )}
      </Stack>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {modalTitle}
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{modalContent}</DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
